import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { IconButton, Tooltip } from '@material-ui/core';
import { Replay } from '@material-ui/icons';
import clsx from 'clsx';
import { FEAT_ROW_LEVELS, FEAT_ROW_POINTS, FeatRanks, IFeat, IFeatClass } from '@pvplevelup/core/dist/feats/FeatTypes';
import { addPoints, canAddPoint, isFeatLocked, pointsBeforeRow, removePoints, resetTree, treePoints } from '@pvplevelup/core/dist/feats/FeatSpec';
import { featAssets } from './featAssets';

// tree geometry (the background art is 998x579 with the three trees side by side)
const TREE_WIDTH = 330;
const TREE_HEIGHT = 579;
const TREE_OFFSETS = [5, 333, 666];
const TOP = 44;
const CELL_WIDTH = TREE_WIDTH / 6;
const CELL_HEIGHT = 59;
const ICON = 38;

const useStyles = makeStyles((theme) => ({
  trees: {
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fit, ${TREE_WIDTH}px)`,
    gridGap: theme.spacing(1),
    justifyContent: 'center',
  },
  tree: {
    position: 'relative',
    width: TREE_WIDTH,
    height: TREE_HEIGHT,
    borderRadius: 10,
    overflow: 'hidden',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 10px 28px rgba(0, 0, 0, 0.35)',
    backgroundRepeat: 'no-repeat',
  },
  treeShort: {
    height: TOP + 5 * CELL_HEIGHT + 12,
  },
  treeHeader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 36,
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto auto',
    alignItems: 'center',
    gridColumnGap: 6,
    padding: '0 6px 0 12px',
    background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.35))',
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
  },
  treeName: {
    fontWeight: 800,
    fontSize: '0.95rem',
    letterSpacing: '0.02em',
    textShadow: '0 1px 2px #000',
  },
  treePoints: {
    minWidth: 30,
    padding: '1px 8px',
    borderRadius: 999,
    textAlign: 'center',
    fontWeight: 800,
    fontSize: '0.85rem',
    color: '#ffd54f',
    background: 'rgba(0, 0, 0, 0.55)',
    border: '1px solid rgba(255, 213, 79, 0.35)',
  },
  reset: {
    padding: 4,
    color: 'rgba(255, 255, 255, 0.7)',
    '& svg': { fontSize: 18 },
  },
  feat: {
    position: 'absolute',
    width: ICON,
    height: ICON + 9,
    userSelect: 'none',
  },
  clickable: {
    cursor: 'pointer',
  },
  icon: {
    position: 'relative',
    zIndex: 2,
    width: ICON,
    height: ICON,
    backgroundRepeat: 'no-repeat',
  },
  unused: {
    opacity: 0.4,
    filter: 'saturate(0.4)',
  },
  border: {
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 3,
    width: ICON,
    height: ICON,
    backgroundImage: `url(${featAssets.border})`,
    backgroundRepeat: 'no-repeat',
  },
  pips: {
    position: 'relative',
    zIndex: 2,
    height: 7,
    margin: '2px auto 0',
    backgroundImage: `url(${featAssets.point})`,
  },
  arrow: {
    position: 'absolute',
    zIndex: 1,
    width: ICON,
    minHeight: ICON,
    backgroundImage: `url(${featAssets.req})`,
    backgroundRepeat: 'no-repeat',
  },
  tip: {
    maxWidth: 300,
    padding: theme.spacing(1.25, 1.5),
    background: 'rgba(12, 12, 16, 0.96)',
    border: '1px solid rgba(255, 255, 255, 0.14)',
    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.5)',
    fontSize: '0.8rem',
    lineHeight: 1.45,
  },
  tipTitle: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gridColumnGap: 10,
    alignItems: 'baseline',
    marginBottom: 6,
    fontWeight: 800,
    fontSize: '0.92rem',
  },
  tipRank: {
    color: '#ffd54f',
    fontSize: '0.8rem',
  },
  tipDesc: {
    color: 'rgba(255, 255, 255, 0.85)',
    '& .g': { color: '#81c784' },
    '& .r': { color: '#e57373' },
    '& .y': { color: '#ffd54f' },
  },
  tipNeeds: {
    marginTop: 8,
    color: '#ffb74d',
  },
  tipHint: {
    marginTop: 8,
    paddingTop: 6,
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: '0.74rem',
  },
}));

interface Props {
  data: IFeatClass;
  ranks: FeatRanks;
  /** editing: click adds a point, right click removes one (with shift or ctrl as many as allowed) */
  onChange?: (ranks: FeatRanks) => void;
}

/** A class's three feat trees: the two class trees and the archetype's general tree. */
export const FeatTrees: React.FC<Props> = ({data, ranks, onChange}) => {
  const classes = useStyles();
  const editable = onChange != null;
  const points = treePoints(data, ranks);
  const byId = (id: number) => data.feats.find((f) => f.id == id);

  // the arrow from a feat's prerequisite: beside it (same row) or above it (one or two rows up)
  const arrow = (feat: IFeat) => {
    const req = feat.req != null ? byId(feat.req) : null;
    if(!req) return null;
    if(req.row == feat.row)
      return req.col < feat.col
        ? <div className={classes.arrow} style={{backgroundPosition: '0 0', left: -28, top: 0}} />
        : <div className={classes.arrow} style={{backgroundPosition: '0 -38px', right: -28, top: 0}} />;
    const rows = feat.row - req.row;
    return <div className={classes.arrow} style={{backgroundPosition: '0 -76px', left: 0, top: rows == 1 ? -38 : -38 - CELL_HEIGHT, minHeight: rows == 1 ? 38 : 38 + CELL_HEIGHT}} />;
  };

  const tooltip = (feat: IFeat, rank: number, locked: boolean) => {
    const needed = feat.row * FEAT_ROW_POINTS;
    const before = pointsBeforeRow(data, ranks, feat.tree, feat.row);
    const req = feat.req != null ? byId(feat.req) : null;
    return (
      <React.Fragment>
        <div className={classes.tipTitle}>
          <span>{feat.name}</span>
          <span className={classes.tipRank}>{rank} / {feat.max}</span>
        </div>
        {/* descriptions only hold <b>, <br> and coloured spans (cleaned when the data was generated) */}
        <div className={classes.tipDesc} dangerouslySetInnerHTML={{__html: feat.desc}} />
        {locked && rank == 0 && (
          <div className={classes.tipNeeds}>
            {before < needed && <div>Needs {needed} points in {data.trees[feat.tree].name} above this row ({before} now)</div>}
            {req && (ranks[req.id] || 0) < req.max && <div>Needs {req.name} at rank {req.max}</div>}
          </div>
        )}
        <div className={classes.tipNeeds} style={{color: 'rgba(255, 183, 77, 0.8)'}}>Level {FEAT_ROW_LEVELS[feat.row]}</div>
        {editable && <div className={classes.tipHint}>Click to add a point, right click to remove one. Hold Shift for all.</div>}
      </React.Fragment>
    );
  };

  const featCell = (feat: IFeat) => {
    const rank = ranks[feat.id] || 0;
    const locked = isFeatLocked(data, ranks, feat);
    const sprite = featAssets.icons(data.trees[feat.tree].general ? data.generalSprite : data.sprite);
    const x = feat.row == 0 ? (TREE_WIDTH - ICON) / 2 : feat.col * CELL_WIDTH + (CELL_WIDTH - ICON) / 2;
    const y = TOP + feat.row * CELL_HEIGHT;
    const bright = rank > 0 || (!locked && editable);
    const borderY = rank >= feat.max ? -76 : !locked ? -38 : 0;
    const add = (e: React.MouseEvent) => onChange && onChange(addPoints(data, ranks, feat, e.shiftKey || e.ctrlKey));
    const remove = (e: React.MouseEvent) => {
      e.preventDefault();
      onChange && onChange(removePoints(data, ranks, feat, e.shiftKey || e.ctrlKey));
    };
    return (
      <Tooltip key={feat.id} title={tooltip(feat, rank, locked)} classes={{tooltip: classes.tip}} placement={data.trees[feat.tree].general ? 'left' : 'right'} enterDelay={150}>
        <div className={clsx(classes.feat, {[classes.clickable]: editable && (canAddPoint(data, ranks, feat) || rank > 0), [classes.unused]: !editable && rank == 0})}
          style={{left: x, top: y}} onClick={editable ? add : undefined} onContextMenu={editable ? remove : undefined}>
          {arrow(feat)}
          <div className={classes.icon} style={{backgroundImage: `url(${sprite})`, backgroundPosition: `-${feat.pic * ICON}px ${bright ? -ICON : 0}px`}} />
          <div className={classes.border} style={{backgroundPosition: `0 ${borderY}px`}} />
          <div className={classes.pips} style={{width: 8 * feat.max, backgroundPosition: `0 -${7 * rank}px`}} />
        </div>
      </Tooltip>
    );
  };

  return (
    <div className={classes.trees}>
      {data.trees.map((tree, i) => (
        <div key={tree.name} className={clsx(classes.tree, {[classes.treeShort]: tree.general})}
          style={{backgroundImage: `url(${featAssets.background(data.sprite)})`, backgroundPosition: `-${TREE_OFFSETS[i]}px 0`}}>
          <div className={classes.treeHeader}>
            <span className={classes.treeName}>{tree.name}</span>
            <span className={classes.treePoints}>{points[i]}</span>
            {editable && (
              <Tooltip title={`Reset ${tree.name}`}>
                <span>
                  <IconButton className={classes.reset} disabled={points[i] == 0} onClick={() => onChange && onChange(resetTree(data, ranks, i))}><Replay /></IconButton>
                </span>
              </Tooltip>
            )}
          </div>
          {data.feats.filter((f) => f.tree == i).map(featCell)}
        </div>
      ))}
    </div>
  );
};
